/**
 * Settings building blocks, shaped like the phone app's settings: grouped cards of rows,
 * each row a coloured rounded-square icon + title + one muted line (the current value).
 */
import type { CSSProperties, ReactNode } from "react";
import { IconBack } from "@veto/ui";
import { ModalShell } from "../../components/ModalShell";

/** Icon tints shared with the phone app (settings_screen.dart). */
export const TINT = {
  blue: "#0A84FF",
  green: "#2FB879",
  coral: "#FF6B5E",
  teal: "#14B8C4",
  purple: "#8B6CF6",
  slate: "#6B7A90",
  amber: "#F6B852",
  danger: "#F4586E",
} as const;

export function IconBadge({ icon, color }: { icon: ReactNode; color: string }) {
  return (
    <span className="sv-badge" style={{ "--sv-badge": color } as CSSProperties} aria-hidden>
      {icon}
    </span>
  );
}

function RowText({ title, subtitle }: { title: ReactNode; subtitle?: ReactNode }) {
  return (
    <span className="sv-row-text">
      <span className="sv-row-title">{title}</span>
      {subtitle != null && subtitle !== "" && <span className="sv-row-sub">{subtitle}</span>}
    </span>
  );
}

function Chevron() {
  return (
    <span className="sv-chevron" aria-hidden>
      ›
    </span>
  );
}

/** A grouped card of rows, with an optional small caps title and a footer note. */
export function Group({
  title,
  footer,
  children,
}: {
  title?: string;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="sv-group" aria-label={title}>
      {title && <h4 className="sv-group-title">{title}</h4>}
      <div className="sv-group-card">{children}</div>
      {footer && <p className="sv-group-footer">{footer}</p>}
    </section>
  );
}

/** A tappable row that opens a page, a chooser or runs an action. */
export function NavRow({
  icon,
  color,
  title,
  subtitle,
  onClick,
  badge,
  danger,
  active,
  disabled,
  id,
  chevron = true,
}: {
  icon: ReactNode;
  color: string;
  title: string;
  subtitle?: ReactNode;
  onClick?: () => void;
  badge?: number;
  danger?: boolean;
  active?: boolean;
  disabled?: boolean;
  id?: string;
  chevron?: boolean;
}) {
  return (
    <button
      type="button"
      id={id}
      className={`sv-row sv-row-button${danger ? " is-danger" : ""}${active ? " is-active" : ""}`}
      onClick={onClick}
      disabled={disabled}
      aria-current={active ? "page" : undefined}
    >
      <IconBadge icon={icon} color={danger ? TINT.danger : color} />
      <RowText title={title} subtitle={subtitle} />
      {badge ? (
        <span className="sv-count" aria-label={`${badge} to review`}>
          {badge}
        </span>
      ) : null}
      {chevron && onClick && <Chevron />}
    </button>
  );
}

/** Native checkbox styled as a switch — keeps keyboard + QA `checkbox` semantics. */
export function SwitchRow({
  id,
  icon,
  color,
  title,
  subtitle,
  checked,
  onChange,
  disabled,
}: {
  id: string;
  icon: ReactNode;
  color: string;
  title: string;
  subtitle?: ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className={`sv-row sv-row-switch${disabled ? " is-disabled" : ""}`} htmlFor={id}>
      <IconBadge icon={icon} color={color} />
      <RowText title={title} subtitle={subtitle} />
      <input
        id={id}
        type="checkbox"
        className="switch"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}

/** Read-only row: an explanation or a value. */
export function InfoRow({
  icon,
  color,
  title,
  subtitle,
  value,
}: {
  icon: ReactNode;
  color: string;
  title: string;
  subtitle?: ReactNode;
  value?: ReactNode;
}) {
  return (
    <div className="sv-row sv-row-info">
      <IconBadge icon={icon} color={color} />
      <RowText title={title} subtitle={subtitle} />
      {value != null && <span className="sv-row-value">{value}</span>}
    </div>
  );
}

/** A row holding its own control underneath (segmented choices, forms). */
export function BlockRow({
  icon,
  color,
  title,
  subtitle,
  children,
}: {
  icon: ReactNode;
  color: string;
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="sv-row sv-row-block">
      <div className="sv-row-block-head">
        <IconBadge icon={icon} color={color} />
        <RowText title={title} subtitle={subtitle} />
      </div>
      <div className="sv-row-block-body">{children}</div>
    </div>
  );
}

/** Segmented single choice (theme, text size). */
export function Segmented<T extends string>({
  label,
  name,
  value,
  options,
  onChange,
}: {
  label: string;
  name: string;
  value: T;
  options: ReadonlyArray<readonly [T, string]>;
  onChange: (v: T) => void;
}) {
  return (
    <div className="seg sv-seg" role="radiogroup" aria-label={label}>
      {options.map(([v, text]) => (
        <label key={v} className={value === v ? "active" : ""}>
          <input
            type="radio"
            name={name}
            value={v}
            checked={value === v}
            onChange={() => onChange(v)}
          />
          {text}
        </label>
      ))}
    </div>
  );
}

/** Page header: Back + title. `section` pages hide Back on wide screens (the nav is visible). */
export function PageHead({
  title,
  backLabel,
  onBack,
  section,
  titleId,
}: {
  title: string;
  backLabel: string;
  onBack: () => void;
  section?: boolean;
  /** Lets the panel move focus to the title when a new page opens. */
  titleId?: string;
}) {
  return (
    <div className="sv-page-head">
      <button
        type="button"
        className={`sv-back${section ? " is-section" : ""}`}
        onClick={onBack}
        aria-label={`Back to ${backLabel}`}
      >
        <IconBack size={18} />
        <span>Back</span>
      </button>
      <h3 id={titleId} tabIndex={-1}>
        {title}
      </h3>
    </div>
  );
}

/** Pick one value from a short list (phone's bottom-sheet chooser). */
export function ChoiceSheet<T extends string | number>({
  title,
  note,
  options,
  value,
  onPick,
  onClose,
}: {
  title: string;
  note?: string;
  options: ReadonlyArray<readonly [T, string]>;
  value: T;
  onPick: (v: T) => void;
  onClose: () => void;
}) {
  return (
    <ModalShell
      title={title}
      ariaLabel={title}
      onClose={onClose}
      size="sm"
      className="sv-choice-card"
    >
      {note && <p className="muted text-sm sv-choice-note">{note}</p>}
      <div className="sv-choices" role="radiogroup" aria-label={title}>
        {options.map(([v, label]) => (
          <button
            key={String(v)}
            type="button"
            role="radio"
            aria-checked={v === value}
            className={`sv-choice${v === value ? " is-selected" : ""}`}
            autoFocus={v === value}
            onClick={() => {
              onPick(v);
              onClose();
            }}
          >
            <span className="sv-radio" aria-hidden />
            {label}
          </button>
        ))}
      </div>
    </ModalShell>
  );
}
